import {useMemo} from 'react';
// SELECTORS
import {
  hotelNameSelector,
  multiRoomFlowStatusSelector,
  isEntirePropertySelector,
  roomFiltersDropdownSelector,
  hotelLocationSelector,
  cfhFromMetaInfo,
  searchHotelDetailsSelector,
  luckyDataSelector,
  isAltAccoSelector,
  roomInfoSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';

import {checkinSelectorFn, checkoutSelectorFn} from '@stencil/hotels-common-reducer/src/selectors';
// TYPES

// UTILS
import dateformat from 'dateformat';
// CONFIG
import {getSlashAndShowPriceCg} from '@stencil/hotels-utils/src/get-details-slash-and-show-price';

// HOOKS
import {useSelector} from 'react-redux';
import RoomSelectionHeaderUpsell from '../../../../../../stories/components/hotels/DetailsPageUnif/Rooms/RoomSelectionHeader/RoomSelectionHeaderUpsell';
import Room from '../../../../../../stories/components/hotels/DetailsPageUnif/Rooms/Room/Room';
import useCurrentRoomData from '../../../../../hooks/useCurrentRoomData';

// STYLES
import * as S from './Rooms.styles';

import CartFlow from '../MultiRoom/CartFlow/CartFlow';
import RoomTypeDropdown from '../UpsellFlow/RoomTypeDropdown/RoomTypeDropdown';
import {getBookatZeroOrOneText, isValidArray} from '@stencil/hotels-utils/src/common-utils';

const NoRoomsAvailable = () => (
  <S.NoRoomsAvailableContainer id="dontHaveData">
    <S.NoRoomsAvailableText>No Rooms Available</S.NoRoomsAvailableText>
  </S.NoRoomsAvailableContainer>
);

// 🐨 this component should re render only twice, right now it's happening more than that
const Rooms = ({
  rooms = [],
  // roomsDataFromCg = [],
  filterHeight = 0,
  currentFilters,
  setFilters,
  handlOpenCalendar = () => {},
}) => {
  let roomCount = 0;
  const {activeBankCode} = useCurrentRoomData();

  const multiRoomFlowStatus = useSelector(multiRoomFlowStatusSelector);
  const roomFilterGroup = useSelector(roomFiltersDropdownSelector);
  const hotelLocation = useSelector(hotelLocationSelector);
  const searchRoomHotelDetails = useSelector(searchHotelDetailsSelector);

  //Room Flavors Props
  // const cityId = useSelector(cityIdSelectorFn);
  const checkinJSDate = useSelector(checkinSelectorFn).date;
  const checkoutJSDate = useSelector(checkoutSelectorFn).date;
  const hotelName = useSelector(hotelNameSelector);
  const luckyData = useSelector(luckyDataSelector);
  // const hotelArea = useSelector(hotelAreaSelectorFn);
  // const selectedHotelDetail = useSelector(selectedHotelStatic);
  const cfhValue = useSelector(cfhFromMetaInfo);
  const isEntireProperty = useSelector(isEntirePropertySelector);
  const isAltAcco = useSelector(isAltAccoSelector);
  const roomInfo = useSelector(roomInfoSelector);

  const isMultiRoom = multiRoomFlowStatus?.hasOccupancyRooms;

  const colummnNames = useMemo(() => {
    let type = 'Room Type';
    let options = 'Room Options';
    if (isEntireProperty) {
      return {
        type: 'Property Type',
        options: 'Property Options',
      };
    } else if (isAltAcco) {
      return {
        type: 'Stay Type',
        options: 'Stay Options',
      };
    } else {
      return {
        type,
        options,
      };
    }
  }, [isEntireProperty, isAltAcco]);

  const checkinYYYYMMDD = dateformat(checkinJSDate, 'yyyymmdd');
  const checkoutYYYYMMDD = dateformat(checkoutJSDate, 'yyyymmdd');

  const checkinDateDDMM = dateformat(checkinJSDate, `dd mmm 'yy`);

  // there are no inclusions for any of the room plans
  const noInclusionsAndSplInstructionsForAnyRoom = !rooms.some(room => {
    return (room.rpl || []).some(roomFlavor => {
      return (
        (roomFlavor?.dil && Array.isArray(roomFlavor?.dil) && !!roomFlavor?.dil.length) ||
        (roomFlavor?.ex && Array.isArray(roomFlavor?.ex) && !!roomFlavor?.ex.length) ||
        (roomFlavor?.rip && Array.isArray(roomFlavor?.rip) && !!roomFlavor?.rip.length)
      );
    });
  });

  const removeBookZeroInclusions = (inclusionsList = []) => {
    let updatedList = [];
    inclusionsList.map(item => {
      if (item?.inclusionCode !== 'FCZPN') {
        updatedList.push(item);
      }
    });
    return updatedList;
  };

  const roomsTpl = useMemo(() => {
    return rooms.map((room, index) => {
      const roomCode = room?.roomCode;
      // 🐨perf: do we even need to have this check, or can we trust voyager that it is always in sync with hermes data

      // 🐨 see how to better handle typescript
      // const staticDataForThisRoom: typeof indexedRoomsStaticData[0] = getStaticDataForThisRoom(
      //   {
      //     indexedRoomsStaticData,
      //     roomCode,
      //   },
      // );

      const roomFlavorsDataForThisRoom = room?.ratePlans;

      const neededRoomFlavorsData = roomFlavorsDataForThisRoom.map(roomFlavor => {
        roomCount += 1;
        const rtfs = roomFlavor?.tariffs?.[0];
        const {
          slashedPrice,
          showPrice,
          payAtPersuasionShowPrice,
          tnc,
          finalPriceKey,
          emiBankDetailsData,
          loginPersuasionPrice,
        } = getSlashAndShowPriceCg(rtfs, activeBankCode);
        const priceData = isMultiRoom ? roomFlavor?.pwi?.[0]?.pd : roomFlavor?.pd;

        // extract this in a separate function
        const ret = {
          ratePlanInfo: roomFlavor,
          roomCount,
          roomFlavorName:
            roomFlavor?.tariffPersuasion?.roomTypeName?.[0]?.persuasionText || roomFlavor?.name,
          roomTypeNameData:
            roomFlavor?.tariffPersuasion?.roomTypeName?.[0]?.persuasionText || roomFlavor?.name,
          refundStatus: roomFlavor?.cancellationPolicy.type,
          refundStatusText: roomFlavor?.cltxt,
          refundStatusTextColor: roomFlavor?.cltxtc,
          amenities: removeBookZeroInclusions(roomFlavor?.inclusionsList),

          ratePlanInclusionPersuation: roomFlavor?.rip,
          exclusions: roomFlavor?.ex,
          discountPersuations: roomFlavor?.dip,

          isRecommended: !!roomFlavor?.rwt,
          slashedPrice,
          showPrice,
          currency: roomFlavor?.cur || '',
          isMemberPrice: roomFlavor?.lib || false,
          showSlashedPrice: !!slashedPrice,
          payAtPersuasionShowPrice,
          uid: roomFlavor?.grpc,
          paxString: priceData?.pax || '',
          emiData: {
            emiStartingPrice: emiBankDetailsData?.amount,
            // emiRates: priceData?.emid,
          },

          tax: tnc,
          rnplPerTxt: roomFlavor?.inclusionsList?.length
            ? getBookatZeroOrOneText(roomFlavor?.inclusionsList)
            : '',
          rnplPer: roomFlavor?.rnplPer || [],
          /** THESE PROPERTIES ARE USED TO MAKE CANCELLATION
           * IF YOU SEE THESE BEING USED ANYWHERE ELSE, REMOVE THIS COMMENT
           */
          forwardParams: roomFlavor?.fwdp,
          roomPriceCode: roomFlavor?.rpc,
          offercode: roomFlavor?.offercode,
          vendor: searchRoomHotelDetails?.supplierId,
          // paymode: roomFlavor?.pm,
          paymode: rtfs?.hermesData?.[0]?.paymentMode,
          roomTypeCode: roomCode,
          /** END OF PREVIOUS COMMENT JURISDICTION */

          gtv: roomFlavor?.gtv,
          idur: roomFlavor?.idur,
          dup: roomFlavor?.dup,
          gseg: roomFlavor?.gseg,
          cot: roomFlavor?.cot,
          hmd: rtfs?.hermesData?.[0]?.paxWiseInfo?.[0]?.hermesMetaData,
          gsv: roomFlavor?.supplierCode,
          pc: finalPriceKey || rtfs?.hermesData?.[0]?.promoCode, // promo code
          dspr: loginPersuasionPrice,
          cancellationPolicyFromHermes: roomFlavor?.cp,
          shouldShowHermesCancellationPolicy: roomFlavor?.is_cp,
          isRnpl: rtfs?.bnplApplicable || 0,
          multiRoomPwi: isMultiRoom ? roomFlavor?.pwi || [] : null,
          sc: roomFlavor?.sc,
          ct: roomFlavor?.ct,
          mt: roomFlavor?.mt,
          perNighDesc:
            !isMultiRoom && priceData?.pDesc && Object.keys(priceData?.pDesc).length > 0
              ? priceData?.pDesc
              : null,
          det_inc: roomFlavor?.det_inc,
          cst: roomFlavor?.cancellationTimeline?.freeCancellationText,

          sellableType: roomFlavor?.sellableType,
          propertyTypeTag: roomInfo?.title,
          tariffs: roomFlavor?.tariffs,
          supplierCode: roomFlavor?.supplierCode,
          cancellationPolicy: roomFlavor?.cancellationPolicy,
          cancellationPolicyTimeline: roomFlavor?.cancellationPolicyTimeline,
          cancellationTimeline: roomFlavor?.cancellationTimeline,
          checkinDateDDMM,
          hermesData: rtfs?.hermesData?.[0] || {},
          ratePlanRoomCount: rtfs?.occupancydetails?.roomCount,
          payModeNew: roomFlavor?.payMode,
          tariffPersuasion: roomFlavor?.tariffPersuasion || {},
          isLucky: roomFlavor?.isLucky,
          goTribeInclusion: roomFlavor?.goTribeInclusion || {},
          isPackageOption: roomFlavor?.packageRateAvailable,
          packageTagImgUrl: searchRoomHotelDetails?.packageTagUrl,
          linkedRatePlans:
            roomFlavor?.linkedRatePlanAvailable &&
            isValidArray(roomFlavor?.linkedRatePlans) &&
            roomFlavor?.linkedRatePlans,
          activeBankCode,
        };

        return ret;
      });

      const roomProps = {
        roomTypeColumnProps: {
          maxNoOfAdults: room?.maxAdultCount,
          roomSize: room?.roomSize,
          bedTypeTNC: room?.bedTypeTnc,
          roomHighlights: room?.roomHighlights,
          imageUrl: room?.images?.[0],
          roomTypeName: room.roomName,
          extraPaxPersuasion: roomFlavorsDataForThisRoom?.[0]?.tariffPersuasion?.extraPaxRpc || [],
          extraBedType: '',
          roomDescription: room?.description,
          roomFacilities: '',
          roomImages: room?.images,
          // noInclusionsAndSplInstructionsForAnyRoom: noInclusionsAndSplInstructionsForAnyRoom,
          roomCode: roomCode,
          // typeCodeMap: typeCodeMap,
          filterHeight,
          reviews: room?.roomReviews,
          amenities: room?.amenities || [],
          // roomViewName:  || '',
          roomPersuations: room?.usp || {},
          hotelName: hotelName,
        },
        roomFlavorProps: {
          cityId: searchRoomHotelDetails?.voyagerCityId,
          checkinJSDate,
          checkoutJSDate,
          isMultiRoom,
          hotelName,
          hotelArea: hotelLocation?.name,
          // selectedHotelDetail,
          cfhValue,
          isEntireProperty,
          checkinYYYYMMDD,
          checkoutYYYYMMDD,
          roomFlavorsList: neededRoomFlavorsData,
          roomTypeName: room.roomName,
          roomDefaultImage: room?.images?.[0],
          roomIndex: index,
          noInclusionsAndSplInstructionsForAnyRoom,
          luckyData,
          handlOpenCalendar: handlOpenCalendar,
        },
        // roomFlavorsList: neededRoomFlavorsData,
      };

      return (
        <Room
          key={`${roomCode}-${index}`}
          noInclusionsAndSplInstructionsForAnyRoom={noInclusionsAndSplInstructionsForAnyRoom}
          {...roomProps}
          index={index}
          filterHeight={filterHeight}
        />
      );
    });
  }, [rooms, activeBankCode, isMultiRoom, searchRoomHotelDetails]);

  if (multiRoomFlowStatus?.isOnlyRecommendedCombos) {
    return null;
  }

  return (
    <S.RoomsStyledContainer>
      <>
        <RoomSelectionHeaderUpsell
          filterHeight={filterHeight}
          isMultiRoom={isMultiRoom}
          upSellRoomTypeDD={
            rooms?.length > 1 ? (
              <RoomTypeDropdown rooms={rooms} colummnNames={colummnNames} />
            ) : null
          }
          currentFilters={currentFilters}
          setFilters={setFilters}
          roomFilterGroup={roomFilterGroup}
          colummnNames={colummnNames}
        />

        {!rooms.length ? (
          <NoRoomsAvailable />
        ) : (
          <S.FlexWrap>
            <S.RightInnerWrapper isMultiRoom={isMultiRoom}>{roomsTpl}</S.RightInnerWrapper>
            {isMultiRoom && (
              <S.RightDiv noInclusions={noInclusionsAndSplInstructionsForAnyRoom}>
                <CartFlow
                  filterHeight={filterHeight}
                  noInclusionsAndSplInstructionsForAnyRoom={
                    noInclusionsAndSplInstructionsForAnyRoom
                  }
                />
              </S.RightDiv>
            )}
          </S.FlexWrap>
        )}
      </>
    </S.RoomsStyledContainer>
  );
};

export default Rooms;
